import { BaseApiClient } from "@/lib/api/baseApiClient.service";
import {
	type User as FirebaseUser,
	getAuth,
	sendEmailVerification,
	updateProfile,
	verifyBeforeUpdateEmail,
} from "firebase/auth";
import { toast } from "sonner";

/**
 * Represents a navigation link with a label and a URL
 */
export interface NavLink {
	label: string;
	href: string;
}

/**
 * Service for managing user profile and user-specific operations
 * Also supports fetching additional user data from the backend, like organizations and navigation links
 */
class UserService {
	private static instance: UserService;
	private auth = getAuth();
	static baseApi: BaseApiClient;

	// all requests to the user service are prefixed with this subpath
	static baseApiSubPath = "/session";

	public static getInstance(): UserService {
		if (!UserService.instance) {
			UserService.instance = new UserService();
			UserService.baseApi = BaseApiClient.getInstance();
		}
		return UserService.instance;
	}

	/**
	 * Get the navigation links for the user
	 * @returns The navigation links for the user
	 */
	public async getNavLinks(): Promise<NavLink[]> {
		return UserService.baseApi.get<NavLink[]>(
			`${UserService.baseApiSubPath}/navigation-links`,
			{ _t: Date.now() }, // cache busting to avoid caching the navigation links
		);
	}

	/**
	 * Send a verification email to the user
	 */
	public async sendEmailVerification(): Promise<void> {
		const currentUser = this.auth.currentUser;
		if (!currentUser) {
			throw new Error("No user is currently signed in");
		}

		await sendEmailVerification(currentUser);
		toast.success("Bestätigungsemail wurde erfolgreich an dich gesendet.");
	}

	/**
	 * This function updates the user's display name via firebase
	 * @param newDisplayName
	 */
	public async updateUserDisplayName(newDisplayName: string): Promise<boolean> {
		try {
			const currentUser = this.auth.currentUser;

			if (!currentUser) {
				throw new Error("No user is currently signed in");
			}

			// update the display name on firebase
			await updateProfile(currentUser, { displayName: newDisplayName });

			// update the display name on the backend
			// this will trigger a refresh of the user data from firebase on the backend
			await UserService.baseApi.get<void>(
				`${UserService.baseApiSubPath}/refreshDisplayName`,
			);
			return Promise.resolve(true);
		} catch (error: unknown) {
			if (error instanceof Error) {
				toast.error("Fehler beim Aktualisieren des Anzeigenamen");
				return Promise.resolve(false);
			}
			return Promise.reject("Fehler beim Aktualisieren des Anzeigenamen");
		}
	}

	/**
	 * Updates the current user's email address
	 *
	 * @param newEmail - The new email address
	 * @returns A promise that resolves when the verification email has been sent
	 * @throws Error if the email update process fails
	 */
	public async updateUserEmail(newEmail: string): Promise<boolean> {
		try {
			const currentUser = this.auth.currentUser;

			if (!currentUser) {
				throw new Error("No user is currently signed in");
			}

			// Configure the action code settings with redirect URL
			const actionCodeSettings = {
				url: `${window.location.origin}/`,
				handleCodeInApp: true,
			};

			// Send verification email to the new address before updating with redirect URL
			await verifyBeforeUpdateEmail(currentUser, newEmail, actionCodeSettings);

			toast.success(
				`Bestätigungsmail wurde an ${newEmail} gesendet. Bitte überprüfe deine E-Mails und bestätige die Änderung.`,
			);

			return Promise.resolve(true);
		} catch (error: unknown) {
			if (error instanceof Error) {
				// Handle specific error cases
				if (error.message.includes("auth/requires-recent-login")) {
					toast.error(
						"Für diese Aktion ist eine erneute Anmeldung erforderlich. Bitte melde dich ab und wieder an.",
					);
				} else {
					toast.error("Fehler beim Aktualisieren der Email-Adresse");
					return Promise.resolve(false);
				}
			}
			return Promise.reject("Fehler beim Aktualisieren der Email-Adresse");
		}
	}

	/**
	 * Get the current Firebase user
	 * @returns The current Firebase user or null if no user is signed in
	 */
	public getCurrentUser(): FirebaseUser | null {
		return this.auth.currentUser;
	}
}

export default UserService;
